/**
 * Mobile Barrier Control - Phase 1A
 * 
 * Safe, audited manual barrier control for operational emergencies.
 * Uses EXISTING barrier command queue system.
 * 
 * SECURITY:
 * - All commands pass source: "mobile_ops"
 * - All commands require confirmation modal
 * - LOCK requires double confirmation (strongest protection)
 * - Commands disabled while pending
 * - Double-tap protection (300ms debounce)
 * - Shows actual command status from backend
 * 
 * PROTECTED: Does NOT modify barrier routing, ANPR logic, or backend command system.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Shield, RefreshCw, Wifi, WifiOff, Lock, Unlock, ArrowUp, ArrowDown,
  CheckCircle, XCircle, AlertTriangle, Clock, History
} from "lucide-react";
import MobileHeader from "./components/MobileHeader";
import StatusIndicator from "./components/StatusIndicator";

const API = "/api";

// Canonical barriers
const BARRIERS = [
  { id: "krs-entry", name: "Entry Barrier", direction: "entry" },
  { id: "krs-exit", name: "Exit Barrier", direction: "exit" },
  { id: "krs-combo", name: "Combo Barrier", direction: "combo" },
];

// Debounce time to prevent double-taps
const DEBOUNCE_MS = 300;

// Command polling timeout: 30 seconds
// After this duration without a terminal result, show "Result Not Confirmed"
const COMMAND_TIMEOUT_MS = 30000;

// Poll interval for command status
const POLL_INTERVAL_MS = 2000;

export default function MobileOpsControl() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bridgeOnline, setBridgeOnline] = useState(false);
  const [bridgeLastHeartbeat, setBridgeLastHeartbeat] = useState(null);
  const [barrierStatus, setBarrierStatus] = useState({});
  const [recentCommands, setRecentCommands] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Command state
  const [pendingCommand, setPendingCommand] = useState(null); // {barrierId, action, commandId, startTime}
  const [commandResult, setCommandResult] = useState(null); // {success, error}
  const [commandTimedOut, setCommandTimedOut] = useState(false); // True if polling timed out
  const [requiresAcknowledge, setRequiresAcknowledge] = useState(false); // Must acknowledge before new command
  
  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState(null); // {barrierId, action, barrierName}
  const [lockConfirmText, setLockConfirmText] = useState("");
  
  // Debounce ref
  const lastCommandTime = useRef(0);
  const pollIntervalRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    fetchAllData();
    
    return () => {
      // Cleanup polling and timeout on unmount
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Setup polling when pendingCommand changes
  useEffect(() => {
    // Clear existing polling/timeout
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    if (pendingCommand?.commandId) {
      // Start polling
      pollIntervalRef.current = setInterval(() => {
        pollCommandStatus(pendingCommand.commandId);
      }, POLL_INTERVAL_MS);
      
      // Start timeout
      timeoutRef.current = setTimeout(() => {
        handleCommandTimeout();
      }, COMMAND_TIMEOUT_MS);
    }
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [pendingCommand?.commandId]);

  // Handle command timeout - called when COMMAND_TIMEOUT_MS reached without terminal result
  const handleCommandTimeout = () => {
    // Stop polling
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    
    setCommandTimedOut(true);
    setRequiresAcknowledge(true);
    setPendingCommand(null);
    
    // Refresh barrier status to show current state
    fetchAllData();
    
    toast.warning("Command submitted — result not confirmed. Please verify barrier status.", {
      duration: 10000,
    });
  };

  // Acknowledge timeout and allow new commands
  const acknowledgeTimeout = () => {
    setCommandTimedOut(false);
    setRequiresAcknowledge(false);
    fetchAllData(); // Refresh status
  };

  const fetchAllData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [healthRes, statusRes, commandsRes] = await Promise.allSettled([
        axios.get(`${API}/barrier-bridge/health`, { withCredentials: true }),
        axios.get(`${API}/barrier-bridge/status`, { withCredentials: true }),
        axios.get(`${API}/barrier-bridge/commands/recent?limit=10`, { withCredentials: true })
      ]);

      // Bridge health
      if (healthRes.status === "fulfilled") {
        setBridgeOnline(healthRes.value.data?.is_online || false);
        setBridgeLastHeartbeat(healthRes.value.data?.last_heartbeat);
      }

      // Barrier status
      if (statusRes.status === "fulfilled") {
        const barriers = statusRes.value.data?.barriers || [];
        const statusMap = {};
        barriers.forEach(b => {
          statusMap[b.id] = b;
        });
        setBarrierStatus(statusMap);
      }

      // Recent commands
      if (commandsRes.status === "fulfilled") {
        setRecentCommands(commandsRes.value.data?.commands || []);
      }

      setLastUpdate(new Date());
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load barrier status");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const pollCommandStatus = async (commandId) => {
    try {
      const res = await axios.get(`${API}/barrier-bridge/command/${commandId}`, { 
        withCredentials: true 
      });
      const cmd = res.data;
      
      if (cmd.status === "completed" || cmd.status === "failed") {
        // Clear timeout since we got a terminal result
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        
        setCommandResult({
          success: cmd.success,
          error: cmd.error
        });
        setPendingCommand(null);
        setCommandTimedOut(false);
        setRequiresAcknowledge(false);
        fetchAllData(); // Refresh status
        
        if (cmd.success) {
          toast.success("Command executed successfully");
        } else {
          toast.error(`Command failed: ${cmd.error || "Unknown error"}`);
        }
      }
    } catch (error) {
      console.error("Failed to poll command:", error);
    }
  };

  const handleAction = (barrierId, action, barrierName) => {
    // Must acknowledge timeout before new command
    if (requiresAcknowledge) {
      toast.error("Please acknowledge the previous command status before issuing a new command.");
      return;
    }
    // Debounce check
    const now = Date.now();
    if (now - lastCommandTime.current < DEBOUNCE_MS) {
      return;
    }
    lastCommandTime.current = now;
    
    // Bridge must be online
    if (!bridgeOnline) {
      toast.error("Bridge is offline. Cannot send commands.");
      return;
    }
    
    // No command while another is pending
    if (pendingCommand) {
      toast.error("Command in progress. Please wait.");
      return;
    }
    
    // Show confirmation modal
    setConfirmModal({ barrierId, action, barrierName });
    setLockConfirmText("");
    setCommandResult(null);
  };

  const executeCommand = async () => {
    const { barrierId, action, barrierName } = confirmModal;
    
    // For LOCK, require typing LOCK
    if (action === "lock" && lockConfirmText.toUpperCase() !== "LOCK") {
      toast.error("Please type LOCK to confirm");
      return;
    }
    
    setConfirmModal(null);
    setPendingCommand({ barrierId, action, commandId: null });
    
    try {
      const res = await axios.post(`${API}/barrier-bridge/command/queue`, {
        barrier_id: barrierId,
        action: action,
        reason: `Mobile Ops: Manual ${action} command`,
        source: "mobile_ops" // CRITICAL: Identifies this as mobile manual command
      }, { withCredentials: true });
      
      const commandId = res.data?.command_id;
      if (commandId) {
        setPendingCommand({ barrierId, action, commandId });
        toast.info(`Command queued: ${action.toUpperCase()} ${barrierName}`);
      } else {
        throw new Error("No command ID returned");
      }
    } catch (error) {
      console.error("Failed to queue command:", error);
      
      // Handle 423 Locked
      if (error.response?.status === 423) {
        toast.error("Barrier locked by system. Contact on-site engineer.");
      } else {
        toast.error(`Failed to queue command: ${error.response?.data?.detail || error.message}`);
      }
      
      setPendingCommand(null);
    }
  };

  const getBarrierStatusDisplay = (barrier) => {
    const status = barrierStatus[barrier.id];
    if (!status) return { status: "unknown", label: "Unknown" };
    
    if (status.locked) {
      return { status: "critical", label: "LOCKED" };
    }
    if (status.coils?.lift) {
      return { status: "pass", label: "OPEN" };
    }
    return { status: "info", label: "CLOSED" };
  };

  const formatTime = (isoString) => {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleTimeString("en-GB", { 
      hour: "2-digit", 
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return formatTime(isoString);
    }
    return date.toLocaleDateString("en-GB", { 
      day: "numeric", 
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white pb-20">
      <MobileHeader
        title="Barrier Control"
        subtitle={bridgeOnline ? "Bridge Online" : "Bridge Offline"}
        onBack={() => navigate("/mobile")}
        onRefresh={fetchAllData}
        refreshing={refreshing}
      />

      {/* Bridge Status Bar */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        bridgeOnline ? "bg-green-900/30" : "bg-red-900/30"
      }`}>
        <div className="flex items-center gap-2">
          {bridgeOnline ? (
            <Wifi className="w-5 h-5 text-green-500" />
          ) : (
            <WifiOff className="w-5 h-5 text-red-500" />
          )}
          <span className={`font-medium ${bridgeOnline ? "text-green-400" : "text-red-400"}`}>
            {bridgeOnline ? "Bridge Online" : "Bridge Offline - Commands Disabled"}
          </span>
        </div>
        {lastUpdate && (
          <span className="text-xs text-slate-400">
            {formatTime(lastUpdate.toISOString())}
          </span>
        )}
      </div>

      {/* Offline Warning */}
      {!bridgeOnline && (
        <div className="mx-4 mt-4 bg-red-900/30 border border-red-700 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-400">Barrier Bridge Offline</p>
              <p className="text-sm text-red-300/70 mt-1">
                All barrier commands are disabled. Contact on-site engineer to restore connectivity.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Command Timeout Acknowledgement Warning */}
      {commandTimedOut && requiresAcknowledge && (
        <div className="mx-4 mt-4 bg-yellow-900/30 border border-yellow-700 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-yellow-400">Command Submitted — Result Not Confirmed</p>
              <p className="text-sm text-yellow-300/70 mt-1">
                The command may have reached the local bridge, but confirmation was not received within 30 seconds.
                The physical barrier state is not confirmed.
              </p>
              <p className="text-sm text-yellow-300/70 mt-2">
                Please verify the barrier status below before issuing another command.
              </p>
              <button
                onClick={acknowledgeTimeout}
                className="mt-3 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-semibold active:scale-95 transition-all"
                data-testid="acknowledge-timeout"
              >
                I Have Verified Barrier Status
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="p-4 space-y-4">
        {/* Barrier Cards */}
        <section className="space-y-4" data-testid="barrier-list">
          {BARRIERS.map((barrier) => {
            const statusDisplay = getBarrierStatusDisplay(barrier);
            const status = barrierStatus[barrier.id];
            const isPending = pendingCommand?.barrierId === barrier.id;
            const isLocked = status?.locked;
            
            return (
              <div 
                key={barrier.id}
                className={`bg-slate-800 rounded-xl p-4 ${
                  isLocked ? "border-2 border-red-500" : ""
                }`}
                data-testid={`barrier-${barrier.id}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Shield className={`w-6 h-6 ${
                      statusDisplay.status === "pass" ? "text-green-500" :
                      statusDisplay.status === "critical" ? "text-red-500" : "text-blue-500"
                    }`} />
                    <div>
                      <p className="font-semibold">{barrier.name}</p>
                      <p className="text-xs text-slate-400">{barrier.id}</p>
                    </div>
                  </div>
                  <StatusIndicator {...statusDisplay} />
                </div>

                {/* Status Details */}
                <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                  <div className="bg-slate-700/50 rounded-lg p-2">
                    <p className="text-xs text-slate-400">Last Action</p>
                    <p className="font-medium">{status?.lastAction?.toUpperCase() || "—"}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-2">
                    <p className="text-xs text-slate-400">Latency</p>
                    <p className="font-medium">{status?.latencyMs ? `${status.latencyMs}ms` : "—"}</p>
                  </div>
                </div>

                {/* Command Pending Indicator */}
                {isPending && (
                  <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3 mb-4 flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                    <div>
                      <p className="font-medium text-blue-400">Command Pending</p>
                      <p className="text-xs text-blue-300/70">
                        {pendingCommand.action?.toUpperCase()} - Waiting for execution...
                      </p>
                    </div>
                  </div>
                )}

                {/* Action Buttons - 2x2 Grid */}
                <div className="grid grid-cols-2 gap-2">
                  {/* LIFT Button */}
                  <button
                    onClick={() => handleAction(barrier.id, "lift", barrier.name)}
                    disabled={!bridgeOnline || isPending || requiresAcknowledge}
                    className="flex flex-col items-center justify-center gap-1 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:opacity-50 rounded-lg p-3 min-h-[70px] active:scale-95 transition-all"
                    data-testid={`${barrier.id}-lift`}
                  >
                    <ArrowUp className="w-6 h-6" />
                    <span className="text-sm font-bold">LIFT</span>
                  </button>

                  {/* CLOSE Button */}
                  <button
                    onClick={() => handleAction(barrier.id, "close", barrier.name)}
                    disabled={!bridgeOnline || isPending || requiresAcknowledge}
                    className="flex flex-col items-center justify-center gap-1 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-700 disabled:opacity-50 rounded-lg p-3 min-h-[70px] active:scale-95 transition-all"
                    data-testid={`${barrier.id}-close`}
                  >
                    <ArrowDown className="w-6 h-6" />
                    <span className="text-sm font-bold">CLOSE</span>
                  </button>

                  {/* UNLOCK Button */}
                  <button
                    onClick={() => handleAction(barrier.id, "unlock", barrier.name)}
                    disabled={!bridgeOnline || isPending || !isLocked || requiresAcknowledge}
                    className={`flex flex-col items-center justify-center gap-1 rounded-lg p-3 min-h-[70px] active:scale-95 transition-all ${
                      isLocked 
                        ? "bg-yellow-600 hover:bg-yellow-700" 
                        : "bg-slate-700 opacity-50"
                    } disabled:bg-slate-700 disabled:opacity-50`}
                    data-testid={`${barrier.id}-unlock`}
                  >
                    <Unlock className="w-6 h-6" />
                    <span className="text-sm font-bold">UNLOCK</span>
                  </button>

                  {/* LOCK Button */}
                  <button
                    onClick={() => handleAction(barrier.id, "lock", barrier.name)}
                    disabled={!bridgeOnline || isPending || isLocked || requiresAcknowledge}
                    className={`flex flex-col items-center justify-center gap-1 rounded-lg p-3 min-h-[70px] active:scale-95 transition-all ${
                      !isLocked 
                        ? "bg-red-600 hover:bg-red-700" 
                        : "bg-slate-700 opacity-50"
                    } disabled:bg-slate-700 disabled:opacity-50`}
                    data-testid={`${barrier.id}-lock`}
                  >
                    <Lock className="w-6 h-6" />
                    <span className="text-sm font-bold">LOCK</span>
                  </button>
                </div>
              </div>
            );
          })}
        </section>

        {/* Recent Commands */}
        <section className="bg-slate-800 rounded-xl p-4" data-testid="recent-commands">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2 mb-3">
            <History className="w-4 h-4" /> Recent Commands
          </h3>
          
          {recentCommands.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-4">No recent commands</p>
          ) : (
            <div className="space-y-2">
              {recentCommands.slice(0, 5).map((cmd, idx) => (
                <div 
                  key={cmd.command_id || idx}
                  className={`bg-slate-700/50 rounded-lg p-3 flex items-center justify-between ${
                    cmd.status === "failed" || cmd.success === false ? "border-l-4 border-red-500" : ""
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{cmd.action?.toUpperCase()}</span>
                      <span className="text-xs text-slate-400">{cmd.barrier_id}</span>
                      {cmd.source === "mobile_ops" && (
                        <span className="text-xs bg-blue-600/30 text-blue-400 px-1.5 py-0.5 rounded">
                          MOBILE
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">
                      {cmd.user_email || "System"} · {formatDate(cmd.queued_at || cmd.created_at)}
                    </p>
                  </div>
                  <StatusIndicator 
                    status={
                      cmd.success === true ? "pass" : 
                      cmd.success === false || cmd.status === "failed" ? "critical" :
                      cmd.status === "completed" ? "pass" :
                      cmd.status === "pending" || cmd.status === "queued" ? "warning" : "unknown"
                    }
                    label={cmd.status?.toUpperCase() || (cmd.success ? "OK" : "—")}
                    size="sm"
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="text-center">
              {confirmModal.action === "lock" ? (
                <Lock className="w-12 h-12 mx-auto mb-3 text-red-500" />
              ) : confirmModal.action === "unlock" ? (
                <Unlock className="w-12 h-12 mx-auto mb-3 text-yellow-500" />
              ) : confirmModal.action === "close" ? (
                <ArrowDown className="w-12 h-12 mx-auto mb-3 text-orange-500" />
              ) : (
                <ArrowUp className="w-12 h-12 mx-auto mb-3 text-green-500" />
              )}
              
              <h3 className="text-xl font-bold">
                Confirm {confirmModal.action?.toUpperCase()}
              </h3>
              <p className="text-slate-400 mt-2">
                {confirmModal.barrierName}
              </p>
            </div>

            {/* Warning for LOCK */}
            {confirmModal.action === "lock" && (
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                <p className="text-red-400 text-sm font-medium mb-3">
                  Warning: This will LOCK the barrier and prevent automatic operation.
                </p>
                <p className="text-slate-400 text-sm mb-3">
                  Type <span className="font-mono font-bold text-white">LOCK</span> to confirm:
                </p>
                <input
                  type="text"
                  value={lockConfirmText}
                  onChange={(e) => setLockConfirmText(e.target.value)}
                  placeholder="Type LOCK"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white uppercase font-mono text-center"
                  autoFocus
                />
              </div>
            )}

            {/* Standard confirmation message */}
            {confirmModal.action !== "lock" && (
              <p className="text-slate-400 text-center">
                Are you sure you want to {confirmModal.action?.toUpperCase()} this barrier?
              </p>
            )}

            {/* Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="bg-slate-700 hover:bg-slate-600 rounded-lg py-3 font-semibold active:scale-95 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={executeCommand}
                disabled={confirmModal.action === "lock" && lockConfirmText.toUpperCase() !== "LOCK"}
                className={`rounded-lg py-3 font-semibold active:scale-95 transition-all disabled:opacity-50 ${
                  confirmModal.action === "lock" 
                    ? "bg-red-600 hover:bg-red-700" 
                    : confirmModal.action === "unlock"
                      ? "bg-yellow-600 hover:bg-yellow-700"
                      : confirmModal.action === "close"
                        ? "bg-orange-600 hover:bg-orange-700"
                        : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {confirmModal.action?.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
